import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loading, Modal, ModalHeader, ModalBody, ButtonTooltip } from '@evertrue/et-components';
import EverTrue from 'app';
import useContactCadences from '../hooks/use-contact-cadences';
import useCadences from '../hooks/use-cadences';
import useCadenceMutations from '../hooks/use-cadence-mutations';
import useCadence from '../hooks/use-cadence';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { CADENCE_MEMBER_ACTIVITY_QUERY } from '../utils/cadence-queries';
import { STATUS_COLORS } from '../utils/member-status';
import { isBulkMessageType, getDisplayStatusLabel } from '../utils/cadence-enums';
import { describeRecipientSuccess, METRIC_TYPE_LABELS } from '../utils/cadence-data-mappers';
import AllUsersStore from 'apps/users/stores/all-users-store';
import { fetchUsersById } from 'apps/volunteers/vms-queries';
import CadenceFlowVisualization from './contact-cadences/cadence-flow-visualization';
import ExitConditionsSection from './contact-cadences/exit-conditions-section';
import {
  CURRENT_COLUMNS,
  HISTORY_COLUMNS,
  loadContactColVisibility,
  saveContactColVisibility,
  formatDate,
  formatShortDate,
  computeDaysActive,
  getFirstName,
  getTypeBadge,
  isCurrent,
} from './contact-cadences/contact-cadences-utils';

/**
 * Renders text with "User #ID" tokens replaced by real user names.
 * Fetches asynchronously; shows raw text until resolved.
 */
const UserResolvedText = ({ text }) => {
  const [resolved, setResolved] = useState(text || '');
  useEffect(() => {
    if (!text) {
      setResolved('');
      return;
    }
    const matches = [...text.matchAll(/User #(\d+)/g)];
    if (!matches.length) {
      setResolved(text);
      return;
    }
    const ids = [...new Set(matches.map((m) => Number(m[1])))];
    fetchUsersById(ids)
      .then((users) => {
        let r = text;
        (users || []).forEach((u) => {
          if (u?.name && u.name !== 'Removed User') {
            r = r.replace(new RegExp(`User #${u.id}`, 'g'), u.name);
          }
        });
        setResolved(r);
      })
      .catch(() => setResolved(text));
  }, [text]);
  return <>{resolved}</>;
};

UserResolvedText.propTypes = {
  text: PropTypes.string,
};

const ContactColsPopover = ({ columns, visibility, onChange, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);
  return (
    <div ref={ref} className="cadence-recipients-table--manage-cols-popover">
      <div className="cadence-recipients-table--manage-cols-header">Manage Columns</div>
      {columns
        .filter((c) => !c.alwaysVisible)
        .map((col) => (
          <label key={col.field} className="cadence-recipients-table--manage-cols-row">
            <input
              type="checkbox"
              checked={visibility[col.field] !== false}
              onChange={(e) => onChange(col.field, e.target.checked)}
            />
            {col.label}
          </label>
        ))}
    </div>
  );
};

ContactColsPopover.propTypes = {
  columns: PropTypes.array.isRequired,
  visibility: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Active cadence detail modal.
 * Shows status badge, enrollment date, goal bar, next action, and flow visualization.
 */
const ActiveCadenceModal = ({ isOpen, row, onClose, onNavigate, onRemove, onSkipStep, contact }) => {
  const { cadence, loading, error: cadenceError } = useCadence(isOpen ? row?.cadenceId : null);
  const [memberActivities, setMemberActivities] = useState([]);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !row?.cadenceMemberId) return;
    cadenceGqlCall(CADENCE_MEMBER_ACTIVITY_QUERY, { cadenceMemberId: String(row.cadenceMemberId) })
      .then((data) => {
        const nodes = (data?.cadenceMemberActivity?.edges || []).map((e) => e.node);
        setMemberActivities(nodes);
      })
      .catch(() => setMemberActivities([]));
  }, [isOpen, row?.cadenceMemberId]);

  if (!isOpen || !row) return null;

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await onRemove(row.cadenceId, row.cadenceMemberId);
      setConfirmRemove(false);
      onClose();
    } catch {
      // onRemove handles the error toast
    } finally {
      setRemoving(false);
    }
  };

  const handleSkipStep = async () => {
    if (!onSkipStep) return;
    setSkipping(true);
    try {
      await onSkipStep(row.cadenceId, row.cadenceMemberId);
      onClose();
    } catch {
      // onSkipStep handles the error toast
    } finally {
      setSkipping(false);
    }
  };

  const isActive = row.displayStatus === 'in_progress' || row.displayStatus === 'enrolled';
  const currentEvent = cadence?.events?.find((e) => String(e.id) === String(row.currentEventId));
  const currentType = (currentEvent?.eventType || '').toLowerCase();
  const isWaitStep = currentEvent && currentType === 'wait';
  const isManualTaskStep =
    currentEvent && (currentType === 'task' || (currentType === 'email' && currentEvent.isAutomated === false));
  const canSkipStep = isWaitStep || isManualTaskStep;
  const firstName = contact?.first_name || contact?.name?.split(' ')[0] || 'Contact';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <div className="cadence-profile-modal cadence-profile-modal--active">
        {/* Header */}
        <div className="cadence-profile-modal--header">
          <div className="cadence-profile-modal--header-left">
            <Icon icon="cadences" iconKit="gt2" className="cadence-profile-modal--header-icon" />
            <div>
              <button
                type="button"
                className="cadence-profile-modal--title-link"
                onClick={() => onNavigate(row.cadenceId, row.cadenceType)}
              >
                {row.cadenceName}
              </button>
              <div className="cadence-profile-modal--header-meta">
                Started {formatDate(row.enrolledAt)}
                <span
                  className={`cadence-profile-modal--status-dot ${
                    row.displayStatus === 'paused'
                      ? 'is-paused'
                      : row.displayStatus === 'blocked'
                        ? 'is-blocked'
                        : 'is-active'
                  }`}
                />
                <span
                  style={{
                    color:
                      row.displayStatus === 'paused'
                        ? '#d97706'
                        : row.displayStatus === 'blocked'
                          ? '#991b1b'
                          : '#16a34a',
                    fontWeight: 600,
                  }}
                >
                  {row.displayStatus === 'paused' ? 'Paused' : row.displayStatus === 'blocked' ? 'Blocked' : 'Active'}
                </span>
              </div>
            </div>
          </div>
          <button type="button" className="cadence-profile-modal--close" onClick={onClose}>
            <Icon icon="close" />
          </button>
        </div>

        {/* Goal section */}
        {row.goalDescription && (
          <div className="cadence-profile-modal--goal-section">
            <div className="cadence-profile-modal--goal-header">
              <span className="cadence-profile-modal--goal-label">GOAL FOR {firstName.toUpperCase()}</span>
              <span
                className={`cadence-profile-modal--goal-badge ${row.isSuccessful ? 'is-reached' : 'is-not-reached'}`}
              >
                {row.isSuccessful ? 'Success Reached' : 'Success Not Reached'}
              </span>
            </div>
            <span className="cadence-profile-modal--goal-desc">{row.goalDescription}</span>
          </div>
        )}

        {/* Cadence Flow — collapsible */}
        <div className="cadence-profile-modal--collapsible">
          <button
            type="button"
            className="cadence-profile-modal--collapsible-header"
            onClick={() => setFlowOpen((v) => !v)}
          >
            <Icon icon="list" />
            <span>CADENCE FLOW</span>
            {row.nextActionName && (
              <span className="cadence-profile-modal--flow-countdown">
                <Icon icon="schedule" />{' '}
                {/^(wait|start|end)\b/i.test(row.nextActionName)
                  ? null
                  : /^condition\b/i.test(row.nextActionName)
                    ? 'Condition'
                    : row.nextActionName}
              </span>
            )}
            <Icon icon={flowOpen ? 'expand-less' : 'expand-more'} className="cadence-profile-modal--chevron" />
          </button>
          {flowOpen && (
            <div className="cadence-profile-modal--collapsible-body">
              <CadenceFlowVisualization
                cadence={cadence}
                loading={loading}
                error={cadenceError}
                activities={memberActivities}
                currentEventId={row.currentEventId}
                memberStatus={row.status}
              />
            </div>
          )}
        </div>

        {/* Exit Conditions — collapsible */}
        {cadence?.autoRemovalRules?.length > 0 && (
          <div className="cadence-profile-modal--collapsible">
            <button
              type="button"
              className="cadence-profile-modal--collapsible-header"
              onClick={() => setExitOpen((v) => !v)}
            >
              <Icon icon="exit-to-app" />
              <span>EXIT CONDITIONS</span>
              <Icon icon={exitOpen ? 'expand-less' : 'expand-more'} className="cadence-profile-modal--chevron" />
            </button>
            {exitOpen && (
              <div className="cadence-profile-modal--collapsible-body">
                <ExitConditionsSection
                  rules={cadence.autoRemovalRules}
                  contactName={contact?.name || contact?.first_name || 'This contact'}
                  cadenceName={row.cadenceName}
                />
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="cadence-profile-modal--footer">
          {confirmRemove ? (
            <div className="cadence-profile-modal--confirm-row">
              <span>Remove from this cadence?</span>
              <Button type="destroy" onClick={handleRemove} disabled={removing}>
                {removing ? 'Removing...' : 'Yes, Remove'}
              </Button>
              <Button type="secondary" onClick={() => setConfirmRemove(false)} disabled={removing}>
                Cancel
              </Button>
            </div>
          ) : (
            <>
              {onSkipStep && isActive && canSkipStep && (
                <Button type="secondary" onClick={handleSkipStep} disabled={skipping || !cadence?.canEdit}>
                  <Icon icon="double-chevron-right" />{' '}
                  {skipping ? 'Skipping...' : isManualTaskStep ? 'Skip Task' : 'Skip Wait'}
                </Button>
              )}
              {onRemove && isActive && (
                <Button type="destroy" onClick={() => setConfirmRemove(true)} disabled={!cadence?.canEdit}>
                  Remove from Cadence
                </Button>
              )}
              <Button type="secondary" onClick={onClose}>
                Close
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

ActiveCadenceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  onSkipStep: PropTypes.func,
  contact: PropTypes.object,
};

/**
 * History cadence detail modal (PM mockup layout).
 *
 * Structure:
 * 1. Header: cadence icon + name (bold) + date range + X close
 * 2. Goal banner: rounded card with colored left accent
 * 3. Event type badge: collapsible pill ("EXIT EVENT" / "Success event")
 * 4. Date grid: 3 equal bordered columns (START DATE | END DATE | HOW IT ENDED)
 * 5. Cadence Flow: collapsible table-like layout via CadenceFlowVisualization
 * 6. Engagement Summary: 2-column key/value list
 * 7. Footer: "Read-only" label + Close button
 */
const HistoryCadenceModal = ({ isOpen, row: rawRow, onClose, onNavigate, contact }) => {
  const { cadence, loading, error: cadenceError } = useCadence(isOpen ? rawRow?.cadenceId : null);
  const [memberActivities, setMemberActivities] = useState([]);
  const [flowOpen, setFlowOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [successExpanded, setSuccessExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen || !rawRow?.cadenceMemberId) return;
    cadenceGqlCall(CADENCE_MEMBER_ACTIVITY_QUERY, { cadenceMemberId: String(rawRow.cadenceMemberId) })
      .then((data) => {
        const nodes = (data?.cadenceMemberActivity?.edges || []).map((e) => e.node);
        setMemberActivities(nodes);
      })
      .catch(() => setMemberActivities([]));
  }, [isOpen, rawRow?.cadenceMemberId]);

  if (!isOpen || !rawRow) return null;

  const row = rawRow;
  const firstName = contact?.first_name || contact?.name?.split(' ')[0] || 'This contact';
  const endDate = row.removedAt || row.completedAt || row.successAchievedAt;

  const dateRangeText = [formatShortDate(row.enrolledAt), formatShortDate(endDate)].filter(Boolean).join(' \u2013 ');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <div className="cadence-profile-modal cadence-profile-modal--history">
        {/* Header */}
        <div className="cadence-profile-modal--header">
          <div className="cadence-profile-modal--header-left">
            <Icon icon="cadences" iconKit="gt2" className="cadence-profile-modal--header-icon" />
            <div className="cadence-profile-modal--header-text">
              <button
                type="button"
                className="cadence-profile-modal--title-link"
                onClick={() => onNavigate(row.cadenceId, row.cadenceType)}
              >
                {row.cadenceName}
              </button>
              {dateRangeText && <span className="cadence-profile-modal--header-dates">{dateRangeText}</span>}
            </div>
          </div>
          <button type="button" className="cadence-profile-modal--close" onClick={onClose}>
            <Icon icon="close" />
          </button>
        </div>

        {/* Fields — vertical layout matching PM prototype */}
        <div className="cadence-profile-modal--field-grid cadence-profile-modal--field-grid-vertical">
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">Start Date</span>
            <span className="cadence-profile-modal--field-value">{formatDate(row.enrolledAt)}</span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">End Date</span>
            <span className="cadence-profile-modal--field-value">{formatDate(endDate)}</span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">Status</span>
            <span className="cadence-profile-modal--field-value">
              {(() => {
                const sd = STATUS_COLORS[row.displayStatus] || { bg: '#f3f4f6', color: '#6b7280' };
                const pill = (
                  <span className="contact-cadences-tab--status-pill" style={{ background: sd.bg, color: sd.color }}>
                    {getDisplayStatusLabel(row)}
                  </span>
                );
                return row.statusExplanation ? (
                  <ButtonTooltip contentWidth={280} trigger={pill}>
                    <p className="cadence-profile-modal--tooltip-body">
                      <UserResolvedText text={row.statusExplanation} />
                    </p>
                  </ButtonTooltip>
                ) : (
                  pill
                );
              })()}
            </span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">Cadence Success</span>
            <span className="cadence-profile-modal--field-value">
              {row.goalDescription ? (
                <button
                  type="button"
                  className="contact-cadences-tab--status-pill"
                  style={{
                    ...(row.isSuccessful
                      ? { background: '#dcfce7', color: '#16a34a' }
                      : { background: '#fef3c7', color: '#92400E' }),
                    cursor: 'pointer',
                    border: 'none',
                    font: 'inherit',
                  }}
                  onClick={() => setSuccessExpanded((v) => !v)}
                >
                  {row.isSuccessful ? 'Success Reached' : 'Success Not Reached'}
                  <Icon
                    icon={successExpanded ? 'chevron-up' : 'chevron-down'}
                    style={{ fontSize: 14, marginLeft: 4 }}
                  />
                </button>
              ) : (
                <span className="contact-cadences-tab--achieved-pill-neutral contact-cadences-tab--status-pill">
                  No Metric Set
                </span>
              )}
            </span>
          </div>
          {successExpanded &&
            row.goalDescription &&
            (() => {
              const sm = cadence?.successMetrics?.[0];
              const metricType = sm?.metricType || row.goalDescription?.toLowerCase().replace(/\s+/g, '_');
              const metricLabel = (sm && METRIC_TYPE_LABELS[sm.metricType]) || row.goalDescription;
              const metricConfig = sm?.metricConfig || {};
              const targetPct = sm?.targetPercentage;
              return (
                <div
                  className="cadence-profile-modal--field-row"
                  style={{
                    borderLeft: `3px solid ${row.isSuccessful ? '#16a34a' : '#d97706'}`,
                    paddingLeft: 8,
                    marginTop: 4,
                  }}
                >
                  <div>
                    <span className="cadence-profile-modal--metric-label">SUCCESS METRIC</span>
                    <div className="cadence-profile-modal--metric-name">{metricLabel}</div>
                    {targetPct != null && (
                      <div className="cadence-profile-modal--metric-target">Target: {targetPct}% of recipients</div>
                    )}
                    <div className="cadence-profile-modal--metric-desc">
                      {describeRecipientSuccess(metricType, metricConfig)}
                    </div>
                    {row.isSuccessful && row.successAchievedAt && (
                      <div className="cadence-profile-modal--metric-success-date">
                        <Icon icon="check" style={{ fontSize: 14 }} /> Success Reached on{' '}
                        {new Date(row.successAchievedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                    )}
                    {!row.isSuccessful && (
                      <div className="cadence-profile-modal--metric-not-reached">Success has not been reached yet.</div>
                    )}
                  </div>
                </div>
              );
            })()}
        </div>

        {/* Collapsible Cadence Flow */}
        <button type="button" className="cadence-profile-modal--collapse-toggle" onClick={() => setFlowOpen(!flowOpen)}>
          <Icon icon="list" className="cadence-profile-modal--collapse-icon" />
          <span className="cadence-profile-modal--collapse-label">CADENCE FLOW</span>
          <Icon icon={flowOpen ? 'chevron-up' : 'chevron-down'} className="cadence-profile-modal--collapse-chevron" />
        </button>

        {flowOpen && (
          <CadenceFlowVisualization
            cadence={cadence}
            loading={loading}
            error={cadenceError}
            activities={memberActivities}
            currentEventId={row.currentEventId}
            memberStatus={row.status}
            displayStatus={row.displayStatus}
          />
        )}

        {/* Exit Conditions — show for history cadences too */}
        {cadence?.autoRemovalRules?.length > 0 && (
          <div className="cadence-profile-modal--collapsible">
            <button
              type="button"
              className="cadence-profile-modal--collapsible-header"
              onClick={() => setExitOpen((v) => !v)}
            >
              <Icon icon="arrow-back" />
              <span>EXIT CONDITIONS</span>
              <Icon icon={exitOpen ? 'chevron-up' : 'chevron-down'} style={{ fontSize: 16, marginLeft: 'auto' }} />
            </button>
            {exitOpen && (
              <ExitConditionsSection
                rules={cadence.autoRemovalRules}
                contactName={firstName}
                cadenceName={row.cadenceName}
              />
            )}
          </div>
        )}

        {/* Engagement Summary — derive counts from activities for accuracy */}
        {(() => {
          const acts = memberActivities || [];
          const count = (type) => acts.filter((a) => a.activityType === type).length;
          const sent = row.totalEmailsSent || count('email_sent');
          const delivered = row.totalEmailsDelivered || count('email_delivered');
          const opened = row.totalEmailsOpened || count('email_opened');
          const clicked = row.totalEmailsClicked || count('email_clicked');
          const replied = row.totalEmailsReplied || count('email_replied');
          return (
            <div className="cadence-profile-modal--engagement-summary">
              <p className="cadence-profile-modal--detail-label">ENGAGEMENT SUMMARY</p>
              <div className="cadence-profile-modal--engagement-list">
                <div className="cadence-profile-modal--engagement-list-item">
                  <span className="cadence-profile-modal--engagement-list-label">Emails sent</span>
                  <span className="cadence-profile-modal--engagement-list-value">{sent}</span>
                </div>
                <div className="cadence-profile-modal--engagement-list-item">
                  <span className="cadence-profile-modal--engagement-list-label">Emails delivered</span>
                  <span className="cadence-profile-modal--engagement-list-value">{delivered}</span>
                </div>
                <div className="cadence-profile-modal--engagement-list-item">
                  <span className="cadence-profile-modal--engagement-list-label">Emails opened</span>
                  <span className="cadence-profile-modal--engagement-list-value">{opened}</span>
                </div>
                <div className="cadence-profile-modal--engagement-list-item">
                  <span className="cadence-profile-modal--engagement-list-label">Emails clicked</span>
                  <span className="cadence-profile-modal--engagement-list-value">{clicked}</span>
                </div>
                <div className="cadence-profile-modal--engagement-list-item">
                  <span className="cadence-profile-modal--engagement-list-label">Emails replied</span>
                  <span className="cadence-profile-modal--engagement-list-value">{replied}</span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Footer */}
        <div className="cadence-profile-modal--footer">
          <span className="cadence-profile-modal--footer-readonly">Read-only &middot; Cadence ended</span>
          <Button type="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

HistoryCadenceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  contact: PropTypes.object,
};

/**
 * Bulk message detail modal — simplified layout per PM prototype.
 * Shows metadata fields, status, cadence success, and engagement timeline.
 */
const BulkMessageModal = ({ isOpen, row: rawRow, onClose, onNavigate, contact }) => {
  const { cadence: cadenceData } = useCadence(isOpen ? rawRow?.cadenceId : null);
  const [memberActivities, setMemberActivities] = useState([]);
  const [successExpanded, setSuccessExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen || !rawRow?.cadenceMemberId) return;
    cadenceGqlCall(CADENCE_MEMBER_ACTIVITY_QUERY, { cadenceMemberId: String(rawRow.cadenceMemberId) })
      .then((data) => {
        const nodes = (data?.cadenceMemberActivity?.edges || []).map((e) => e.node);
        setMemberActivities(nodes);
      })
      .catch(() => setMemberActivities([]));
  }, [isOpen, rawRow?.cadenceMemberId]);

  if (!isOpen || !rawRow) return null;

  const row = rawRow;
  const firstName = contact?.contact_attributes?.name_first || contact?.name_first || 'Contact';

  // Find engagement timestamps from activities
  const findActivity = (type) => memberActivities.find((a) => a.activityType === type);
  const sentAct = findActivity('email_sent');
  const deliveredAct = findActivity('email_delivered');
  const openedAct = findActivity('email_opened');
  const clickedAct = findActivity('email_clicked');
  const repliedAct = findActivity('email_replied');

  const formatActivityDate = (act) => {
    if (!act?.activityAt) return null;
    const iso = act.activityAt;
    const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return null;
    return (
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
      ', ' +
      d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
    );
  };

  // Resolve sender name from cadence owner or creator
  const sentByName = (() => {
    const uid = cadenceData?.ownerUserId || cadenceData?.createdByUserId || row.createdByUserId || row.enrolledByUserId;
    if (!uid) return '\u2014';
    try {
      const user = AllUsersStore.getUser(uid);
      return user?.name || `User ${uid}`;
    } catch {
      return `User ${uid}`;
    }
  })();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <div className="cadence-profile-modal cadence-profile-modal--bulk">
        {/* Header */}
        <div className="cadence-profile-modal--header">
          <div className="cadence-profile-modal--header-left">
            <Icon icon="email" className="cadence-profile-modal--header-icon" />
            <div className="cadence-profile-modal--header-text">
              <button
                type="button"
                className="cadence-profile-modal--title-link"
                onClick={() => onNavigate(row.cadenceId, row.cadenceType)}
              >
                {row.cadenceName}
              </button>
            </div>
          </div>
          <button type="button" className="cadence-profile-modal--close" onClick={onClose}>
            <Icon icon="close" />
          </button>
        </div>

        {/* Metadata fields */}
        <div className="cadence-profile-modal--field-grid cadence-profile-modal--field-grid-vertical">
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">
              {(row.cadenceStatus || '').toLowerCase() === 'scheduled' ? 'Scheduled for' : 'Sent On'}
            </span>
            <span className="cadence-profile-modal--field-value">
              {(row.cadenceStatus || '').toLowerCase() === 'scheduled'
                ? formatDate(cadenceData?.scheduledSendDate) || 'Pending'
                : formatActivityDate(sentAct) || formatDate(row.enrolledAt)}
            </span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">
              {(row.cadenceStatus || '').toLowerCase() === 'scheduled' ? 'Created By' : 'Sent By'}
            </span>
            <span className="cadence-profile-modal--field-value">{sentByName}</span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">Channel</span>
            <span className="cadence-profile-modal--field-value">
              <span className="cadence-profile-modal--channel-badge">Email</span>
            </span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">Subject / Name</span>
            <span className="cadence-profile-modal--field-value">{row.cadenceName}</span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">Recipients</span>
            <span className="cadence-profile-modal--field-value">
              {cadenceData?.totalMembers || row.totalContacts || '\u2014'} contacts
            </span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">Status</span>
            <span className="cadence-profile-modal--field-value">
              {(() => {
                const sd = STATUS_COLORS[row.displayStatus] || { bg: '#f3f4f6', color: '#6b7280' };
                return (
                  <span className="contact-cadences-tab--status-pill" style={{ background: sd.bg, color: sd.color }}>
                    {getDisplayStatusLabel(row)}
                  </span>
                );
              })()}
            </span>
          </div>
          <div className="cadence-profile-modal--field-row">
            <span className="cadence-profile-modal--field-label">Cadence Success</span>
            <span className="cadence-profile-modal--field-value">
              {row.goalDescription ? (
                <button
                  type="button"
                  className="contact-cadences-tab--status-pill"
                  style={{
                    ...(row.isSuccessful
                      ? { background: '#dcfce7', color: '#16a34a' }
                      : { background: '#fef3c7', color: '#92400E' }),
                    cursor: 'pointer',
                    border: 'none',
                    font: 'inherit',
                  }}
                  onClick={() => setSuccessExpanded((v) => !v)}
                >
                  {row.isSuccessful ? 'Success Reached' : 'Success Not Reached'}
                  <Icon
                    icon={successExpanded ? 'chevron-up' : 'chevron-down'}
                    style={{ fontSize: 14, marginLeft: 4 }}
                  />
                </button>
              ) : (
                <span className="contact-cadences-tab--achieved-pill-neutral contact-cadences-tab--status-pill">
                  No Metric Set
                </span>
              )}
            </span>
          </div>
          {successExpanded &&
            row.goalDescription &&
            (() => {
              const sm = cadenceData?.successMetrics?.[0];
              const metricType = sm?.metricType || row.goalDescription?.toLowerCase().replace(/\s+/g, '_');
              const metricLabel = (sm && METRIC_TYPE_LABELS[sm.metricType]) || row.goalDescription;
              const metricConfig = sm?.metricConfig || {};
              const targetPct = sm?.targetPercentage;
              return (
                <div
                  className="cadence-profile-modal--field-row"
                  style={{
                    borderLeft: `3px solid ${row.isSuccessful ? '#16a34a' : '#d97706'}`,
                    paddingLeft: 8,
                    marginTop: 4,
                  }}
                >
                  <div>
                    <span className="cadence-profile-modal--metric-label">SUCCESS METRIC</span>
                    <div className="cadence-profile-modal--metric-name">{metricLabel}</div>
                    {targetPct != null && (
                      <div className="cadence-profile-modal--metric-target">Target: {targetPct}% of recipients</div>
                    )}
                    <div className="cadence-profile-modal--metric-desc">
                      {describeRecipientSuccess(metricType, metricConfig)}
                    </div>
                    {row.isSuccessful && row.successAchievedAt && (
                      <div className="cadence-profile-modal--metric-success-date">
                        <Icon icon="check" style={{ fontSize: 14 }} /> Success Reached on{' '}
                        {new Date(row.successAchievedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </div>
                    )}
                    {!row.isSuccessful && (
                      <div className="cadence-profile-modal--metric-not-reached">Success has not been reached yet.</div>
                    )}
                  </div>
                </div>
              );
            })()}
        </div>

        {/* Engagement section */}
        <div className="cadence-profile-modal--engagement-summary">
          <p className="cadence-profile-modal--detail-label">ENGAGEMENT FOR {firstName.toUpperCase()}</p>
          <div className="cadence-profile-modal--engagement-table">
            {[
              { label: 'Sent', act: sentAct },
              { label: 'Delivered', act: deliveredAct },
              { label: 'Opened', act: openedAct },
              { label: 'Clicked', act: clickedAct },
              { label: 'Replied', act: repliedAct },
            ].map(({ label, act }) => (
              <div key={label} className="cadence-profile-modal--engagement-table-row">
                <span className="cadence-profile-modal--engagement-table-label">{label}</span>
                <span className="cadence-profile-modal--engagement-table-value">
                  {act ? (
                    <>
                      <Icon icon="check" style={{ fontSize: 14, color: '#16a34a' }} /> {formatActivityDate(act)}
                    </>
                  ) : (
                    'No'
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="cadence-profile-modal--footer">
          <span className="cadence-profile-modal--footer-readonly">Read-only &middot; Bulk message</span>
          <Button type="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

BulkMessageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  contact: PropTypes.object,
};

/**
 * Modal that lets a user search / pick a cadence to enroll a contact into.
 */
const AddToCadenceModal = ({ isOpen, onClose, contact, onEnrolled }) => {
  const { cadences, loading: cadencesLoading } = useCadences();
  const { enrollMember } = useCadenceMutations();
  const [search, setSearch] = useState('');
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [enrollError, setEnrollError] = useState(null);

  const currentUserId = EverTrue.store?.user?.get('id');
  const activeCadences = cadences.filter((c) => {
    const status = (c.displayStatusLabel || '').toLowerCase();
    // Exclude blueprints/templates (check both flag and name pattern)
    if (c.isTemplate) return false;
    // Only allow regular cadences — exclude bulk messages, prospect plans
    if (isBulkMessageType(c.cadenceType) || c.cadenceType === 'prospect_plan') return false;
    // Only active or paused cadences (exclude draft, archived, completed, scheduled)
    if (status !== 'active' && status !== 'paused') return false;
    // Exclude enrollment-locked cadences — users cannot manually enroll into them
    if (c.enrollmentLocked) return false;
    // Only show cadences where current user is the owner
    if (currentUserId && String(c.ownerUserId) !== String(currentUserId)) return false;
    // Search filter
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleEnroll = async (cadence) => {
    setEnrollError(null);
    setEnrollLoading(true);
    try {
      await enrollMember(cadence.id, contact.id);
      EverTrue.Alert.success(`Enrolled in "${cadence.name}"`);
      onEnrolled();
      onClose();
    } catch (err) {
      setEnrollError(err.message || 'Failed to enroll contact');
    } finally {
      setEnrollLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <ModalHeader title="Add to Cadence 2.0" closeModal={onClose} />
      <ModalBody>
        <div className="contact-cadences-picker">
          <input
            type="text"
            className="contact-cadences-picker--search"
            placeholder="Search cadences..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />

          {enrollError && (
            <div className="contact-cadences-picker--error">
              <Icon icon="error-outline" size={1} />
              {enrollError}
            </div>
          )}

          {cadencesLoading ? (
            <div className="contact-cadences-picker--loading">
              <Loading />
            </div>
          ) : activeCadences.length === 0 ? (
            <div className="contact-cadences-picker--empty">
              {search ? `No cadences matching "${search}"` : 'No active cadences found.'}
            </div>
          ) : (
            <ul className="contact-cadences-picker--list">
              {activeCadences.map((cadence) => (
                <li key={cadence.id} className="contact-cadences-picker--item">
                  <div className="contact-cadences-picker--item-info">
                    <span className="contact-cadences-picker--item-name">{cadence.name}</span>
                    {cadence.description && (
                      <span className="contact-cadences-picker--item-desc">{cadence.description}</span>
                    )}
                  </div>
                  <Button
                    type="secondary"
                    disabled={enrollLoading}
                    onClick={() => handleEnroll(cadence)}
                    className="contact-cadences-picker--enroll-btn"
                  >
                    Enroll
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

AddToCadenceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contact: PropTypes.object.isRequired,
  onEnrolled: PropTypes.func.isRequired,
};

/**
 * "2.0 Cadences" tab on the contact profile page.
 * Two tables: Current Cadence(s) and Cadence History.
 * Clickable rows open detail modals instead of navigating away.
 */
const ContactCadencesTab = ({ contact = {} }) => {
  const { cadences, loading, error, refetch } = useContactCadences(contact.id);
  const { removeCadenceMember, skipCadenceEvent } = useCadenceMutations();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentCollapsed, setCurrentCollapsed] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(5);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(5);
  const [showCurrentCols, setShowCurrentCols] = useState(false);
  const [showHistoryCols, setShowHistoryCols] = useState(false);
  const [currentColVis, setCurrentColVis] = useState(() => loadContactColVisibility('current'));
  const [historyColVis, setHistoryColVis] = useState(() => loadContactColVisibility('history'));
  const isCurrentColVisible = (f) => currentColVis[f] !== false;
  const isHistoryColVisible = (f) => historyColVis[f] !== false;
  const toggleCurrentCol = useCallback((field, visible) => {
    setCurrentColVis((prev) => {
      const next = { ...prev, [field]: visible };
      saveContactColVisibility('current', next);
      return next;
    });
  }, []);
  const toggleHistoryCol = useCallback((field, visible) => {
    setHistoryColVis((prev) => {
      const next = { ...prev, [field]: visible };
      saveContactColVisibility('history', next);
      return next;
    });
  }, []);

  const firstName = getFirstName(contact);

  const { currentCadences, historyCadences } = useMemo(() => {
    const current = [];
    const history = [];
    (cadences || []).forEach((row) => {
      // Exclude draft cadences — they shouldn't appear on the contact profile
      if ((row.cadenceStatus || '').toLowerCase() === 'draft') return;
      if (isCurrent(row)) {
        current.push(row);
      } else {
        history.push(row);
      }
    });
    return { currentCadences: current, historyCadences: history };
  }, [cadences]);

  const handleRowClick = (row) => {
    setSelectedRow(row);
  };

  const handleCloseModal = () => {
    setSelectedRow(null);
  };

  const handleNavigateToCadence = (cadenceId, cadenceType) => {
    const isBulk = isBulkMessageType(cadenceType);
    const path = isBulk ? `/#/cadences-v2/bulk-message/${cadenceId}` : `/#/cadences-v2/${cadenceId}`;
    window.open(path, '_blank');
  };

  const handleSkipStep = async (cadenceId, memberId) => {
    try {
      const result = await skipCadenceEvent(cadenceId, memberId);
      if (result?.success) {
        EverTrue.Alert.success('Step skipped');
        refetch();
      } else {
        EverTrue.Alert.error('Failed to skip step');
      }
    } catch (err) {
      EverTrue.Alert.error(`Failed to skip step: ${err.message}`);
      throw err;
    }
  };

  const handleRemoveMember = async (cadenceId, memberId) => {
    try {
      const result = await removeCadenceMember({
        cadenceId: String(cadenceId),
        memberId: String(memberId),
        removalReason: 'manual',
      });
      if (result?.cadenceMember) {
        EverTrue.Alert.success('Contact removed from cadence');
        refetch();
      } else {
        EverTrue.Alert.error('Failed to remove contact from cadence');
      }
    } catch (err) {
      EverTrue.Alert.error(`Failed to remove: ${err.message}`);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="contact-cadences-tab">
        <div className="contact-cadences-tab--loading">
          <Loading />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="contact-cadences-tab">
        <div className="contact-cadences-tab--error">
          <Icon icon="error-outline" size={2} />
          <p>Failed to load cadence history: {error}</p>
          <Button type="secondary" onClick={refetch}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const noCadences = currentCadences.length === 0 && historyCadences.length === 0;

  return (
    <div className="contact-cadences-tab">
      <div className="contact-cadences-tab--header">
        <h2 className="contact-cadences-tab--title">2.0 Cadences</h2>
        <Button type="action" onClick={() => setPickerOpen(true)}>
          Add to Cadence 2.0
        </Button>
      </div>

      {noCadences ? (
        <div className="contact-cadences-tab--empty">
          <Icon icon="email" size={3} className="contact-cadences-tab--empty-icon" />
          <p className="contact-cadences-tab--empty-text">This contact has not been enrolled in any cadences.</p>
          <Button type="secondary" onClick={() => setPickerOpen(true)}>
            Enroll in a Cadence
          </Button>
        </div>
      ) : (
        <>
          {/* ===== Current Cadence(s) ===== */}
          <div className="contact-cadences-tab--section">
            <button
              type="button"
              className="contact-cadences-tab--section-toggle"
              onClick={() => setCurrentCollapsed((v) => !v)}
            >
              <Icon icon={currentCollapsed ? 'chevron-right' : 'chevron-down'} />
              <h3 className="contact-cadences-tab--section-title">Current Cadence(s) ({currentCadences.length})</h3>
            </button>
            {!currentCollapsed && currentCadences.length === 0 && (
              <p className="contact-cadences-tab--section-empty">Not currently enrolled in any cadences.</p>
            )}
            {!currentCollapsed && currentCadences.length > 0 && (
              <div className="contact-cadences-tab--table-wrap">
                <div className="contact-cadences-tab--table-toolbar">
                  <div className="cadence-recipients-table--manage-cols-wrap">
                    <button
                      type="button"
                      className="cadence-recipients-table--manage-cols-btn"
                      onClick={() => setShowCurrentCols((v) => !v)}
                    >
                      <Icon icon="settings" size={1} /> Columns
                    </button>
                    {showCurrentCols && (
                      <ContactColsPopover
                        columns={CURRENT_COLUMNS}
                        visibility={currentColVis}
                        onChange={toggleCurrentCol}
                        onClose={() => setShowCurrentCols(false)}
                      />
                    )}
                  </div>
                </div>
                <table className="contact-cadences-tab--table">
                  <thead>
                    <tr>
                      <th className="contact-cadences-tab--th">Cadence Name</th>
                      {isCurrentColVisible('type') && <th className="contact-cadences-tab--th">Type</th>}
                      {isCurrentColVisible('status') && <th className="contact-cadences-tab--th">Status</th>}
                      {isCurrentColVisible('startDate') && <th className="contact-cadences-tab--th">Start Date</th>}
                      {isCurrentColVisible('daysActive') && <th className="contact-cadences-tab--th">Days Active</th>}
                      {isCurrentColVisible('nextAction') && <th className="contact-cadences-tab--th">Next Action</th>}
                      {isCurrentColVisible('goal') && (
                        <th className="contact-cadences-tab--th">Goal for {firstName}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {currentCadences
                      .slice(currentPage * currentRowsPerPage, (currentPage + 1) * currentRowsPerPage)
                      .map((row) => {
                        const typeBadge = getTypeBadge(row.cadenceType);
                        return (
                          <tr
                            key={row.cadenceMemberId}
                            className="contact-cadences-tab--row contact-cadences-tab--row-clickable"
                            onClick={() => handleRowClick(row)}
                          >
                            <td
                              className="contact-cadences-tab--td contact-cadences-tab--td-name"
                              title={row.cadenceName}
                            >
                              <Icon icon="users" className="contact-cadences-tab--name-icon" />
                              {row.cadenceName}
                            </td>
                            {isCurrentColVisible('type') && (
                              <td className="contact-cadences-tab--td">
                                <span className={`contact-cadences-tab--type-badge ${typeBadge.className}`}>
                                  {typeBadge.label}
                                </span>
                              </td>
                            )}
                            {isCurrentColVisible('status') && (
                              <td className="contact-cadences-tab--td">
                                {(() => {
                                  const sd = STATUS_COLORS[row.displayStatus] || { bg: '#f3f4f6', color: '#6b7280' };
                                  const pill = (
                                    <span
                                      className="contact-cadences-tab--status-pill"
                                      style={{ background: sd.bg, color: sd.color }}
                                    >
                                      {getDisplayStatusLabel(row)}
                                    </span>
                                  );
                                  return row.statusExplanation ? (
                                    <ButtonTooltip contentWidth={280} trigger={pill}>
                                      <p className="cadence-profile-modal--tooltip-body">
                                        <UserResolvedText text={row.statusExplanation} />
                                      </p>
                                    </ButtonTooltip>
                                  ) : (
                                    pill
                                  );
                                })()}
                              </td>
                            )}
                            {isCurrentColVisible('startDate') && (
                              <td className="contact-cadences-tab--td">
                                {isBulkMessageType(row.cadenceType) &&
                                (row.cadenceStatus || '').toLowerCase() === 'scheduled'
                                  ? formatDate(row.nextActionDate) || formatDate(row.enrolledAt)
                                  : formatDate(row.enrolledAt)}
                              </td>
                            )}
                            {isCurrentColVisible('daysActive') && (
                              <td className="contact-cadences-tab--td">{computeDaysActive(row.enrolledAt)}</td>
                            )}
                            {isCurrentColVisible('nextAction') && (
                              <td className="contact-cadences-tab--td">
                                {isBulkMessageType(row.cadenceType) &&
                                (row.cadenceStatus || '').toLowerCase() === 'scheduled' ? (
                                  <span>
                                    Scheduled Send
                                    {row.nextActionDate && (
                                      <span className="contact-cadences-tab--next-date">
                                        {' \u2013 '}
                                        {formatShortDate(row.nextActionDate)}
                                      </span>
                                    )}
                                  </span>
                                ) : row.nextActionName ? (
                                  <span>
                                    {/^(wait|start|end)\b/i.test(row.nextActionName)
                                      ? '\u2014'
                                      : /^condition\b/i.test(row.nextActionName)
                                        ? 'Condition'
                                        : row.nextActionName}
                                    {row.nextActionDate && (
                                      <span className="contact-cadences-tab--next-date">
                                        {' \u2013 '}
                                        {formatShortDate(row.nextActionDate)}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  '\u2014'
                                )}
                              </td>
                            )}
                            {isCurrentColVisible('goal') && (
                              <td className="contact-cadences-tab--td">
                                {row.goalDescription ? (
                                  <div className="contact-cadences-tab--goal">
                                    <span className="contact-cadences-tab--goal-desc">{row.goalDescription}</span>
                                    <span
                                      className="contact-cadences-tab--achieved-pill"
                                      style={
                                        row.isSuccessful
                                          ? { background: '#dcfce7', color: '#16a34a' }
                                          : { background: '#fef3c7', color: '#92400E' }
                                      }
                                    >
                                      {row.isSuccessful ? <>Success Reached</> : 'Success Not Reached'}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="contact-cadences-tab--achieved-pill contact-cadences-tab--achieved-pill-neutral">
                                    No Metric Set
                                  </span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {currentCadences.length > 0 && (
                  <div className="contact-cadences-tab--pagination">
                    <span>
                      {currentPage * currentRowsPerPage + 1}–
                      {Math.min((currentPage + 1) * currentRowsPerPage, currentCadences.length)} of{' '}
                      {currentCadences.length}
                    </span>
                    <div className="contact-cadences-tab--pagination-btns">
                      <button type="button" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>
                        &lsaquo;
                      </button>
                      {Array.from({ length: Math.ceil(currentCadences.length / currentRowsPerPage) }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          className={currentPage === i ? 'is-active' : ''}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        type="button"
                        disabled={(currentPage + 1) * currentRowsPerPage >= currentCadences.length}
                        onClick={() => setCurrentPage((p) => p + 1)}
                      >
                        &rsaquo;
                      </button>
                    </div>
                    <div className="contact-cadences-tab--rows-per-page">
                      Rows:
                      <select
                        value={currentRowsPerPage}
                        onChange={(e) => {
                          setCurrentRowsPerPage(Number(e.target.value));
                          setCurrentPage(0);
                        }}
                      >
                        <option value={5}>5 / page</option>
                        <option value={10}>10 / page</option>
                        <option value={25}>25 / page</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ===== Cadence History ===== */}
          <div className="contact-cadences-tab--section">
            <button
              type="button"
              className="contact-cadences-tab--section-toggle"
              onClick={() => setHistoryCollapsed((v) => !v)}
            >
              <Icon icon={historyCollapsed ? 'chevron-right' : 'chevron-down'} />
              <h3 className="contact-cadences-tab--section-title">Cadence History ({historyCadences.length})</h3>
            </button>
            {!historyCollapsed && historyCadences.length === 0 && (
              <p className="contact-cadences-tab--section-empty">No cadence history yet.</p>
            )}
            {!historyCollapsed && historyCadences.length > 0 && (
              <div className="contact-cadences-tab--table-wrap">
                <div className="contact-cadences-tab--table-toolbar">
                  <div className="cadence-recipients-table--manage-cols-wrap">
                    <button
                      type="button"
                      className="cadence-recipients-table--manage-cols-btn"
                      onClick={() => setShowHistoryCols((v) => !v)}
                    >
                      <Icon icon="settings" size={1} /> Columns
                    </button>
                    {showHistoryCols && (
                      <ContactColsPopover
                        columns={HISTORY_COLUMNS}
                        visibility={historyColVis}
                        onChange={toggleHistoryCol}
                        onClose={() => setShowHistoryCols(false)}
                      />
                    )}
                  </div>
                </div>
                <table className="contact-cadences-tab--table">
                  <thead>
                    <tr>
                      <th className="contact-cadences-tab--th">Cadence Name</th>
                      {isHistoryColVisible('type') && <th className="contact-cadences-tab--th">Type</th>}
                      {isHistoryColVisible('status') && <th className="contact-cadences-tab--th">Status</th>}
                      {isHistoryColVisible('startDate') && <th className="contact-cadences-tab--th">Start Date</th>}
                      {isHistoryColVisible('endDate') && <th className="contact-cadences-tab--th">End Date</th>}
                      {isHistoryColVisible('goal') && (
                        <th className="contact-cadences-tab--th">Goal for {firstName}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {historyCadences
                      .slice(historyPage * historyRowsPerPage, (historyPage + 1) * historyRowsPerPage)
                      .map((rawRow) => {
                        const row = rawRow;
                        const typeBadge = getTypeBadge(row.cadenceType);
                        const endDate = row.removedAt || row.completedAt || row.successAchievedAt;

                        return (
                          <tr
                            key={row.cadenceMemberId}
                            className="contact-cadences-tab--row contact-cadences-tab--row-clickable"
                            onClick={() => handleRowClick(row)}
                          >
                            <td
                              className="contact-cadences-tab--td contact-cadences-tab--td-name"
                              title={row.cadenceName}
                            >
                              <Icon icon="users" className="contact-cadences-tab--name-icon" />
                              {row.cadenceName}
                            </td>
                            {isHistoryColVisible('type') && (
                              <td className="contact-cadences-tab--td">
                                <span className={`contact-cadences-tab--type-badge ${typeBadge.className}`}>
                                  {typeBadge.label}
                                </span>
                              </td>
                            )}
                            {isHistoryColVisible('status') && (
                              <td className="contact-cadences-tab--td">
                                {(() => {
                                  const sd = STATUS_COLORS[row.displayStatus] || { bg: '#f3f4f6', color: '#6b7280' };
                                  const pill = (
                                    <span
                                      className="contact-cadences-tab--status-pill"
                                      style={{ background: sd.bg, color: sd.color }}
                                    >
                                      {getDisplayStatusLabel(row)}
                                    </span>
                                  );
                                  return row.statusExplanation ? (
                                    <ButtonTooltip contentWidth={280} trigger={pill}>
                                      <p className="cadence-profile-modal--tooltip-body">
                                        <UserResolvedText text={row.statusExplanation} />
                                      </p>
                                    </ButtonTooltip>
                                  ) : (
                                    pill
                                  );
                                })()}
                              </td>
                            )}
                            {isHistoryColVisible('startDate') && (
                              <td className="contact-cadences-tab--td">{formatDate(row.enrolledAt)}</td>
                            )}
                            {isHistoryColVisible('endDate') && (
                              <td className="contact-cadences-tab--td">{formatDate(endDate)}</td>
                            )}
                            {isHistoryColVisible('goal') && (
                              <td className="contact-cadences-tab--td">
                                {row.goalDescription ? (
                                  <>
                                    <div>{row.goalDescription}</div>
                                    <span
                                      className="contact-cadences-tab--achieved-pill"
                                      style={
                                        row.isSuccessful
                                          ? { background: '#dcfce7', color: '#16a34a' }
                                          : { background: '#fef3c7', color: '#92400E' }
                                      }
                                    >
                                      {row.isSuccessful ? <>Success Reached</> : 'Success Not Reached'}
                                    </span>
                                  </>
                                ) : (
                                  <span className="contact-cadences-tab--achieved-pill contact-cadences-tab--achieved-pill-neutral">
                                    No Metric Set
                                  </span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {historyCadences.length > 0 && (
                  <div className="contact-cadences-tab--pagination">
                    <span>
                      {historyPage * historyRowsPerPage + 1}–
                      {Math.min((historyPage + 1) * historyRowsPerPage, historyCadences.length)} of{' '}
                      {historyCadences.length}
                    </span>
                    <div className="contact-cadences-tab--pagination-btns">
                      <button type="button" disabled={historyPage === 0} onClick={() => setHistoryPage((p) => p - 1)}>
                        &lsaquo;
                      </button>
                      {Array.from({ length: Math.ceil(historyCadences.length / historyRowsPerPage) }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          className={historyPage === i ? 'is-active' : ''}
                          onClick={() => setHistoryPage(i)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        type="button"
                        disabled={(historyPage + 1) * historyRowsPerPage >= historyCadences.length}
                        onClick={() => setHistoryPage((p) => p + 1)}
                      >
                        &rsaquo;
                      </button>
                    </div>
                    <div className="contact-cadences-tab--rows-per-page">
                      Rows:
                      <select
                        value={historyRowsPerPage}
                        onChange={(e) => {
                          setHistoryRowsPerPage(Number(e.target.value));
                          setHistoryPage(0);
                        }}
                      >
                        <option value={5}>5 / page</option>
                        <option value={10}>10 / page</option>
                        <option value={25}>25 / page</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <AddToCadenceModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        contact={contact}
        onEnrolled={refetch}
      />

      {selectedRow && isCurrent(selectedRow) && !isBulkMessageType(selectedRow.cadenceType) && (
        <ActiveCadenceModal
          isOpen={true}
          row={selectedRow}
          onClose={handleCloseModal}
          onNavigate={handleNavigateToCadence}
          onRemove={selectedRow.canManage !== false ? handleRemoveMember : undefined}
          onSkipStep={selectedRow.canManage !== false ? handleSkipStep : undefined}
          contact={contact}
        />
      )}

      {selectedRow && isBulkMessageType(selectedRow.cadenceType) && (
        <BulkMessageModal
          isOpen={true}
          row={selectedRow}
          onClose={handleCloseModal}
          onNavigate={handleNavigateToCadence}
          contact={contact}
        />
      )}

      {selectedRow && !isCurrent(selectedRow) && !isBulkMessageType(selectedRow.cadenceType) && (
        <HistoryCadenceModal
          isOpen={true}
          row={selectedRow}
          onClose={handleCloseModal}
          onNavigate={handleNavigateToCadence}
          contact={contact}
        />
      )}
    </div>
  );
};

ContactCadencesTab.propTypes = {
  contact: PropTypes.object,
};

export { AddToCadenceModal, CadenceFlowVisualization };
export default ContactCadencesTab;
